import { GetServerSidePropsContext } from "next";
import { ParsedUrlQuery } from "querystring";
import {
  MEMBERSHIP_STEPS_AUTHENTICATED,
  MEMBERSHIP_STEPS_DUMMY_ACCOUNT,
  MEMBERSHIP_STEPS_NO_ACCOUNT,
  MEMBERSHIP_STEPS_SUBSCRIBE_AUTHENTICATED,
  MEMBERSHIP_STEPS_SUBSCRIBE_DUMMY_ACCOUNT,
  MEMBERSHIP_STEPS_SUBSCRIBE_NO_ACCOUNT,
} from "components/Membership/MembershipStepFlow";
import { MEMBERSHIP_STEP_SELECT_PLAN } from "components/Membership/MembershipStepSelectPlan";
import { MEMBERSHIP_STEP_FALL25_PROMO } from "components/Membership/MembershipStepFall25Promo";
import { FEATURE_FLAG_SHOW_FALL25_COUPON } from "components/Membership/membershipFeatureFlags";
import { isDummyUser } from "components/Membership/membershipUtil";
import {
  MembershipSlug,
  MembershipState,
} from "components/Membership/membershipTypes";
import {
  Plans,
  SUBSCRIPTION_PLAN_SLUGS,
} from "components/PlanSelection/planSelectionTypes";
import {
  FALL25_COUPON_CODE,
  isCouponClaimedByQuery,
  isFall25PromoOnSession,
} from "components/Step/stepUtil";
import { CheckoutSession } from "lib/api/checkout";

/**
 * Budget step routes high-budget readers into the Fall25 offer. Omit once they have claimed
 * the offer (URL from reading-level dialog / links) or it is on the cart with the monthly offer plan.
 */
export function shouldOmitBudgetStepForAppliedFall25Coupon({
  checkoutSession,
  plans,
  routerQuery,
}: {
  checkoutSession: CheckoutSession | null | undefined;
  plans?: Plans | null;
  /** Client router query; when Fall25 is in the URL (e.g. after `StepConfirmationReadingLevel` dialog), skip budget on back/forward. */
  routerQuery?: ParsedUrlQuery;
}): boolean {
  if (!FEATURE_FLAG_SHOW_FALL25_COUPON) {
    return false;
  }
  // Reading-level “Claim” dialog (and shared links): extCouponCode is set before select-plan;
  // user should not be sent through budget again on back navigation.
  if (routerQuery && isCouponClaimedByQuery(routerQuery, FALL25_COUPON_CODE)) {
    return true;
  }
  if (!isFall25PromoOnSession(checkoutSession)) {
    return false;
  }
  const selected = checkoutSession?.selectedPlan;
  if (!selected) {
    return false;
  }
  const monthlySlug = plans?.monthly2025?.slug;
  // Select-plan Fall25 path: promo + monthly TBYB offer slug on session.
  if (monthlySlug && selected === monthlySlug) {
    return true;
  }
  return selected === SUBSCRIPTION_PLAN_SLUGS.TBYB_MONTHLY_2025;
}

export type ServerSideRedirectOptionsSubscribeFlow = {
  ctx: GetServerSidePropsContext;
} & Pick<MembershipState, "checkoutSession" | "invoice" | "isAuthenticated">;

export const getServerSideRedirectOptionsSubscribeFlow = ({
  checkoutSession,
  ctx,
  invoice,
  isAuthenticated,
}: ServerSideRedirectOptionsSubscribeFlow) => {
  const { slug: s, ...queries } = ctx.query;
  const slug = s as MembershipSlug;

  let shouldRedirect = false;
  let dest = "";

  if (!slug || slug === "onboarding-confirmation") {
    return { shouldRedirect };
  }

  const dummyUser = isDummyUser({ checkoutSession, isAuthenticated });
  const hasInvoice = Boolean(invoice?.id);

  const queryParamString = new URLSearchParams(
    queries as Record<string, string>
  ).toString();
  const query = Boolean(queryParamString) ? `?${queryParamString}` : "";

  const redirectToRelationship =
    slug !== "relationship" && !dummyUser && !isAuthenticated;

  const redirectToShipping =
    slug !== "shipping" && Boolean(isAuthenticated) && hasInvoice;

  const redirectToPayment =
    slug === "create-account" && isAuthenticated && !hasInvoice;

  shouldRedirect =
    redirectToRelationship || redirectToPayment || redirectToShipping;

  if (redirectToRelationship) {
    dest = `/subscribe/relationship/`;
  } else if (redirectToPayment) {
    dest = `/subscribe/payment/`;
  } else if (redirectToShipping) {
    dest = `/subscribe/shipping/`;
  }

  const destination = `${dest}${query}`;

  if (shouldRedirect) {
    return {
      shouldRedirect,
      redirect: {
        destination,
        permanent: false,
      },
    };
  }

  return {
    shouldRedirect: false,
  };
};

export interface GetSubscribeStepsArgs
  extends Pick<
    MembershipState,
    "checkoutSession" | "childAge" | "isAuthenticated" | "excludeFromAnnual"
  > {
  isAffiliateVisitor: boolean;
  /** When true, `budget` is spliced out after select-plan / fall25 insert logic (see shouldOmitBudgetStepForAppliedFall25Coupon). */
  omitBudgetStep?: boolean;
}

export const getSubscribeSteps = ({
  checkoutSession,
  excludeFromAnnual,
  isAffiliateVisitor,
  isAuthenticated,
  omitBudgetStep = false,
}: GetSubscribeStepsArgs) => {
  const base = isAuthenticated
    ? MEMBERSHIP_STEPS_SUBSCRIBE_AUTHENTICATED
    : checkoutSession && checkoutSession.accountInfo
    ? MEMBERSHIP_STEPS_SUBSCRIBE_DUMMY_ACCOUNT
    : MEMBERSHIP_STEPS_SUBSCRIBE_NO_ACCOUNT;
  // Copy: downstream splices must not mutate the exported MEMBERSHIP_STEPS_* arrays (shared module state).
  const steps = [...base];

  // insert or remove select plan step
  const selectPlanIndex = steps.findIndex((s) => s.slug === "select-plan");
  const selectedPlanStepHasBeenAdded = selectPlanIndex !== -1;

  // insert or remove fall25 promo step
  const fall25PromoIndex = steps.findIndex((s) => s.slug === "fall25-promo");
  const fall25PromoStepHasBeenAdded = fall25PromoIndex !== -1;

  if (excludeFromAnnual) {
    if (selectedPlanStepHasBeenAdded) {
      steps.splice(selectPlanIndex, 1);
    }

    if (isAffiliateVisitor) {
      const idx = steps.findIndex(
        (s) => s.slug === "reading-level-recommendation"
      );

      if (!fall25PromoStepHasBeenAdded) {
        steps.splice(idx + 1, 0, MEMBERSHIP_STEP_FALL25_PROMO);
      }
    }
  } else {
    if (!selectedPlanStepHasBeenAdded) {
      const idx = steps.findIndex(
        (s) => s.slug === "reading-level-recommendation"
      );
      steps.splice(idx + 1, 0, MEMBERSHIP_STEP_SELECT_PLAN);
    }
  }

  // Fall25 claimed (URL or cart): drop budget so onboarding/subscribe back-stack skips it.
  if (omitBudgetStep) {
    const budgetIdx = steps.findIndex((s) => s.slug === "budget");
    if (budgetIdx !== -1) {
      steps.splice(budgetIdx, 1);
    }
  }

  return steps;
};

export type GetMembershipStepsArgs = Pick<
  MembershipState,
  "checkoutSession" | "isAuthenticated" | "excludeFromAnnual"
> & {
  /** Same as getSubscribeSteps: remove `budget` when Fall25 offer is already claimed/applied. */
  omitBudgetStep?: boolean;
};

export const getMembershipSteps = ({
  checkoutSession,
  excludeFromAnnual,
  isAuthenticated,
  omitBudgetStep = false,
}: GetMembershipStepsArgs) => {
  const base = isAuthenticated
    ? MEMBERSHIP_STEPS_AUTHENTICATED
    : checkoutSession && checkoutSession.accountInfo
    ? MEMBERSHIP_STEPS_DUMMY_ACCOUNT
    : MEMBERSHIP_STEPS_NO_ACCOUNT;
  // Copy: do not splice the shared MEMBERSHIP_STEPS_* arrays in place.
  const steps = [...base];

  // insert or remove select plan step
  const selectPlanIndex = steps.findIndex((s) => s.slug === "select-plan");
  const selectedPlanStepHasBeenAdded = selectPlanIndex !== -1;

  if (excludeFromAnnual) {
    if (selectedPlanStepHasBeenAdded) {
      steps.splice(selectPlanIndex, 1);
    }
  } else {
    if (!selectedPlanStepHasBeenAdded) {
      const idx = steps.findIndex(
        (s) => s.slug === "reading-level-recommendation"
      );
      steps.splice(idx + 1, 0, MEMBERSHIP_STEP_SELECT_PLAN);
    }
  }

  // Same Fall25 budget omission as subscribe flow.
  if (omitBudgetStep) {
    const budgetIdx = steps.findIndex((s) => s.slug === "budget");
    if (budgetIdx !== -1) {
      steps.splice(budgetIdx, 1);
    }
  }

  return steps;
};
